import { useRef, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { ReactSVG } from "react-svg";

import Steps from "./create/Steps";
import FirstStep from "./create/FirstStep";
import SecondStep from "./create/SecondStep";
import ThirdStep from "./create/ThirdStep";
import FourthStep from "./create/FourthStep";
import CommonSlider from "../common/CommonSlider";
import DuplicatePatientDialog from "./create/DuplicatePatientDialog";
import ConfirmationModalNew from "../common/ConfirmationModalNew";
import { setPatientDraft, clearPatientDraft } from "../../redux/modules/patient/actions";

import NewToolTip from "../common/New-ToolTip";
import PrimaryPhysician from "../common/tooltip/primary-physician";

export const initialPatientCreateObject = {
    lastName: "",
    middleName: "",
    firstName: "",
    dateOfBirth: "",
    biologicalSex: "",
    gender: "",
    primaryLanguage: "english",
    preferences: "",
    noPhone: "",
    primaryPhoneCountry: "CA",
    phoneNumber: "",
    secondaryPhoneNumber: "",
    secondaryPhoneCountry: "CA",
    email: "",
    noAddress: "",
    longTermCare: "",
    facility: "",
    address: "",
    unit: "",
    poBox: "",
    city: "",
    state: "ontario",
    postalCode: "",
    country: "canada",
    commentsFirstStep: "",
    provincialInsurance: "provincialInsurance",
    healthCardNumber: "",
    healthCardVersionCode: "",
    healthCardExpiryDate: "",
    healthCardProvince: "ontario",
    insurancePlans: [
      {
        insurancePlanName: "",
        orderOfInsurance: "",
        insuranceExpiryDate: "",
        insuranceEffectiveDate: "",
        comments: "",
        isEditing: false,
        isCompleted: false,
        errors: {
          insurancePlanName: "",
          orderOfInsurance: "",
          insuranceExpiryDate: "",
          insuranceEffectiveDate: "",
        },
      },
    ],
    skipCaregiver: false,
    lastNameCaregiver: "",
    firstNameCaregiver: "",
    contactOptionsCaregiver: "",
    phoneCountryCareGiver: "CA",
    secondaryPhoneCountryCareGiver: "CA",
    secondaryPhoneNumberCaregiver: "",
    relationshipTypeCaregiver: "",
    phoneNumberCaregiver: "",
    emailCaregiver: "",
    sameAsPersonalInfoCheckBox: "",
    addressCaregiver: "",
    unitCaregiver: "",
    poBoxCaregiver: "",
    cityCaregiver: "",
    stateCaregiver: "",
    postalCodeCaregiver: "",
    countryOptionsCaregiver: "",
    commentsCaregiver: "",
    careProviders: [
      {
        firstNameCareProvider: "",
        lastNameCareProvider: "",
        licenseNumberCareProvider: "",
        designationCareProvider: "",
        isEditing: true,
        isCompleted: false,
        phoneCountryCareProvider: "CA",
        phoneNumberCareProvider: "",
        faxCareProvider: "",
        emailCareProvider: "",
        addressCareProvider: "",
        unitCareProvider: "",
        poBoxCareProvider: "",
        cityCareProvider: "",
        stateCareProvider: "",
        postalCodeCareProvider: "",
        countryCareProvider: "",
        commentsCareProvider: "",
        errors: {
          firstNameCareProvider: "",
          lastNameCareProvider: "",
          licenseNumberCareProvider: "",
          designationCareProvider: "",
          phoneNumberCareProvider: "",
          faxCareProvider: "",
        },
      },
    ],
}

export default function Create({ open, onClose }) {
  const [currentStep, setCurrentStep] = useState(1);
  const [patientCreateObject, setPatientCreateObject] = useState(() => structuredClone(initialPatientCreateObject));
  const formRef = useRef(null);
  
  // Duplicate patient dialog state
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const [duplicatePatientData, setDuplicatePatientData] = useState(null);
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);
  const dispatch = useDispatch();
  const patientDraft = useSelector((s) => s.patients?.patientDraft || null);

  const steps = [
    { name: "Personal" },
    { name: "Insurance" },
    { name: "Alternate Caregiver" },
    { name: "Physician" },
  ];
  const [showTooltip, setShowTooltip] = useState(false);
  const noteTitle = "";
  const noteSubTitle = "";
  const noteDescription = "";

  // Duplicate patient dialog handlers
  const handleDuplicatePatientFound = (patientData) => {
    setDuplicatePatientData(patientData);
    setShowDuplicateDialog(true);
  };

  const handleDuplicateDialogClose = () => {
    setShowDuplicateDialog(false);
  };

  const handleDuplicateDialogCancel = () => {
    setShowDuplicateDialog(false);
  };

  const handleGoToPatientChart = () => {
    // Close dialog first to prevent content change
    setShowDuplicateDialog(false);
    
    // Navigate to the existing patient's chart
    if (duplicatePatientData && duplicatePatientData.patientId) {
      window.location.href = `/patient/${duplicatePatientData.patientId}`;
    }
  };


  const handleClose = (value) => {
    setPatientCreateObject(structuredClone(initialPatientCreateObject));
    setShowDiscardDialog(false);
    setCurrentStep(1);
    dispatch(clearPatientDraft());
    onClose(value);
  };

  const isPatientFormModified = () => {
    return (
      patientCreateObject.firstName ||
      patientCreateObject.lastName ||
      patientCreateObject.dateOfBirth
    );
  };

  const handleXClose = () => {
    if (isPatientFormModified()) {
      setShowDiscardDialog(true);
    } else {
      handleClose(false);
    }
  };

  useEffect(() => {
    if (open && patientDraft) {
      setPatientCreateObject(structuredClone(patientDraft));
    }
  }, [open, patientDraft]);

  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => {
      dispatch(setPatientDraft(structuredClone(patientCreateObject)));
    }, 500); 
    return () => clearTimeout(timer);
  }, [patientCreateObject, open, dispatch]);

  return (
    <>
      <CommonSlider open={open} onClose={handleXClose}>
        <div className="flex flex-col flex-1">
          <div className="relative flex flex-col flex-1 h-full">
            {/* Header */}
            <div className="px-8 pt-6">
              <div className="flex items-start justify-between space-x-3">
                <div className="flex space-x-1">
                  <p className="font-bold text-gray-900 text-display-xs">
                    Add Patient -
                  </p>
                  <p className="font-normal text-gray-900 text-display-xs flex items-center gap-2">
                    {currentStep === 4 
                      ? "Physician / Primary Care Provider"
                      : `${steps[currentStep - 1].name} Information`}
                    {steps[currentStep - 1].name === "Physician" && (
                      <div className="relative flex gap-2">
                        <ReactSVG
                          src="/icons/information-dark.svg"
                          className="w-5"
                          data-tooltip-id="primary-physician"
                        />
                        <NewToolTip
                          id="primary-physician"
                          render={() => <PrimaryPhysician />}
                        />
                      </div>
                    )}
                  </p>
                </div>
                <div className="flex items-center h-7">
                  <button
                    type="button"
                    className="flex items-center justify-center p-1 transition-all rounded-full max-w-8 cursor-pointer right-4 top-3 bg-blue-200 hover:bg-REELTeal hover:text-white text-teal-300"
                    onClick={() => handleXClose()}
                  >
                    <span className="sr-only">Close panel</span>
                    <XMarkIcon
                      className="w-5 h-5 m-auto"
                      strokeWidth="2"
                      aria-hidden="true"
                    />
                  </button>
                </div>
              </div>
              <div className="flex justify-center pt-12 pb-2">
                <Steps
                  currentStep={currentStep}
                  setCurrentStep={setCurrentStep}
                  stepsList={steps}
                />
              </div>
            </div>
            {currentStep === 1 && (
              <FirstStep
                patientCreateObject={patientCreateObject}
                setPatientCreateObject={setPatientCreateObject}
                currentStep={currentStep}
                setCurrentStep={setCurrentStep}
                onClose={(value) => handleClose(value)}
              />
            )}
            {currentStep === 2 && (
              <SecondStep
                patientCreateObject={patientCreateObject}
                setPatientCreateObject={setPatientCreateObject}
                currentStep={currentStep}
                setCurrentStep={setCurrentStep}
                onClose={(value) => handleClose(value)}
              />
            )}
            {currentStep === 3 && (
              <ThirdStep
                currentStep={currentStep}
                patientCreateObject={patientCreateObject}
                setPatientCreateObject={setPatientCreateObject}
                setCurrentStep={setCurrentStep}
                onClose={(value) => handleClose(value)}
              />
            )}
            {currentStep === 4 && (
              <FourthStep
                currentStep={currentStep}
                patientCreateObject={patientCreateObject}
                setPatientCreateObject={setPatientCreateObject}
                setCurrentStep={setCurrentStep}
                onClose={(value) => handleClose(value)}
              />
            )}
          </div>
        </div>
      </CommonSlider>
      
      {/* Duplicate Patient Dialog - Rendered outside CommonSlider for full-screen modal */}
      <DuplicatePatientDialog
        open={showDuplicateDialog}
        onClose={handleDuplicateDialogClose}
        onCancel={handleDuplicateDialogCancel}
        onGoToPatientChart={handleGoToPatientChart}
        patientName={duplicatePatientData ? `${duplicatePatientData.lastName} ${duplicatePatientData.firstName}` : ''}
        dateOfBirth={duplicatePatientData ? duplicatePatientData.dateOfBirth : ''}
      />

      {/* Discard patient entry confirmation shown when X clicked and form is dirty */}
      <ConfirmationModalNew
        open={showDiscardDialog}
        title="Discard Patient Entry"
        confirmText="Continue to Add"
        cancelText="Discard Patient"
        className="!w-[600px] !max-w-none"
        maskClosable={false}
        content={() => (
          <div className="text-gray-800 text-base text-start">
            <p className="mb-4">You have unsaved information for this patient. If you close this form, the entered information will be lost.</p>
          </div>
        )}
        onCross={() => setShowDiscardDialog(false)}
        handleClose={() => setShowDiscardDialog(false)}
        confirmAction={() => {
          setShowDiscardDialog(false);
        }}
        cancelAction={() => {
          setShowDiscardDialog(false);
          setTimeout(() => handleClose(false), 220);
        }}
      />
    </>
  );
}
